import { useEffect } from "react";

import isEqual from "lodash/isEqual";

import type { IBooleanSearchChipItem } from "@similarweb/ui-components";
import { BooleanSearch, ChipDownContainer, EllipsisDropdownItem } from "@similarweb/ui-components";

import { ColumnsPickerLitePro } from "app/components/ColumnsPickerLitePro/ColumnsPickerLitePro";
import { usePrevious } from "app/components/hooks/usePrevious";
import { CircularLoader } from "app/components/React/CircularLoader/CircularLoader";
import { i18nFilter } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/ngFilters";
import {
    booleanSearchApiParamsToChips,
    booleanSearchChipsObjectToApiParams,
} from "app/pages/website-analysis/traffic-sources/search/booleanSearchUtility";

import type { IOssTableSelectedFilters, IOssTableSelectedWordCount } from "../ConversionSegmentOSSTypes";

import {
    FiltersWrap,
    StyledLabel,
    circularLoaderOptions,
    InnerWrapper,
    ResultCountWapper,
    ChipWrap,
    TopTableFiltersBarContainer,
    StyledBooleanSearchWrapper,
} from "./ConversionSegmentOSSTableTopStyles";

interface IConversionSegmentOSSTableTopProps {
    onFilterChange: (filter: any) => void;
    tableColumns: any;
    onClickToggleColumns: (key: number) => void;
    availableWordCount: [
        {
            Value: number;
            ResultCount: number;
        },
    ];
    isLoadingData: boolean;
    tableOptions: any;

    selectedFilters?: IOssTableSelectedFilters;
    updateSelectedFilters: (selectedFilters?: IOssTableSelectedFilters) => void;

    selectedWordCount?: IOssTableSelectedWordCount;
    updateSelectedWordCount: (wordsCount?: IOssTableSelectedWordCount) => void;
}

export const ConversionSegmentOSSTableTop = ({
    onFilterChange,
    tableColumns,
    onClickToggleColumns,
    availableWordCount,
    isLoadingData,
    tableOptions,
    selectedFilters,
    selectedWordCount,
    updateSelectedFilters,
    updateSelectedWordCount,
}: IConversionSegmentOSSTableTopProps) => {
    const previousFilters = usePrevious<{
        selectedFilters: IOssTableSelectedFilters | undefined;
        selectedWordCount: IOssTableSelectedWordCount | undefined;
    }>({ selectedFilters, selectedWordCount });

    const getSelectedFilters = () => {
        return {
            ...selectedFilters,
            wordCount: selectedWordCount ? selectedWordCount.value : undefined,
        };
    };

    useEffect(() => {
        if (!previousFilters) {
            return;
        }

        if (
            !isEqual(previousFilters.selectedFilters, selectedFilters) ||
            !isEqual(previousFilters.selectedWordCount, selectedWordCount)
        ) {
            onFilterChange(getSelectedFilters());
        }
    }, [selectedFilters, selectedWordCount]);

    function getWordCountText(wordCount: IOssTableSelectedWordCount) {
        if (!wordCount.value) {
            return "conversion.segment.oss.word.count.no.filter";
        }
        return wordCount.value === 1
            ? "conversion.segment.oss.word.count.single.filter"
            : "conversion.segment.oss.word.count.multiple.filter";
    }

    const selectedText = !isLoadingData && selectedWordCount ? getWordCountText(selectedWordCount) : null;
    const onWordCountChange = ({ id }) => {
        if (id === "0") {
            updateSelectedWordCount(undefined);
        }

        const nextType = availableWordCount.find((x) => x.Value.toString() === id);
        updateSelectedWordCount({ resultCount: nextType?.ResultCount, value: nextType?.Value });
    };
    const onCloseType = () => {
        updateSelectedWordCount(undefined);
    };

    const onSearch = (chipsObject: IBooleanSearchChipItem[]) => {
        const { IncludeTerms, ExcludeTerms } = booleanSearchChipsObjectToApiParams(chipsObject);
        updateSelectedFilters({ includeTerms: IncludeTerms, excludeTerms: ExcludeTerms });
    };

    const renderWordCountFilter = () => {
        return (
            <FiltersWrap>
                <StyledLabel>{i18nFilter()("conversion.segment.oss.table.top.filter.label")}</StyledLabel>
                {isLoadingData ? (
                    <CircularLoader options={circularLoaderOptions} />
                ) : (
                    <ChipWrap>
                        <ChipDownContainer
                            onClick={onWordCountChange}
                            selectedIds={selectedWordCount?.value ? { [selectedWordCount.value]: true } : {}}
                            selectedText={
                                selectedWordCount?.value
                                    ? `${selectedWordCount.value} ${i18nFilter()(selectedText)}`
                                    : undefined
                            }
                            onCloseItem={onCloseType}
                            buttonText={i18nFilter()(getWordCountText({ value: 0, resultCount: undefined }))}
                            width={300}
                            noResultsMessage={i18nFilter()("dropdown.no.results")}
                        >
                            {[
                                <EllipsisDropdownItem
                                    key={-1}
                                    id={"0"}
                                >
                                    <InnerWrapper>
                                        <div>
                                            {i18nFilter()(
                                                getWordCountText({
                                                    value: 0,
                                                    resultCount: undefined,
                                                }),
                                            )}
                                        </div>
                                    </InnerWrapper>
                                </EllipsisDropdownItem>,
                            ].concat(
                                availableWordCount.map((wordCount) => {
                                    const label = i18nFilter()(
                                        getWordCountText({
                                            value: wordCount.Value,
                                            resultCount: wordCount.ResultCount,
                                        }),
                                    );
                                    const infoText = (
                                        <ResultCountWapper>({numberFilter()(wordCount.ResultCount)})</ResultCountWapper>
                                    );
                                    return (
                                        <EllipsisDropdownItem
                                            infoText={infoText}
                                            key={wordCount.Value.toString()}
                                            id={wordCount.Value.toString()}
                                        >
                                            <InnerWrapper>
                                                <div>
                                                    {wordCount.Value} {label}
                                                </div>
                                            </InnerWrapper>
                                        </EllipsisDropdownItem>
                                    );
                                }),
                            )}
                        </ChipDownContainer>
                    </ChipWrap>
                )}
            </FiltersWrap>
        );
    };
    return (
        <TopTableFiltersBarContainer>
            {renderWordCountFilter()}
            <StyledBooleanSearchWrapper>
                <BooleanSearch
                    onChange={onSearch}
                    chips={booleanSearchApiParamsToChips({
                        IncludeTerms: selectedFilters?.includeTerms,
                        ExcludeTerms: selectedFilters?.excludeTerms,
                    })}
                />
                <span>
                    <ColumnsPickerLitePro
                        onClickToggleColumns={onClickToggleColumns}
                        tableOptions={tableOptions}
                        tableColumns={tableColumns}
                        withTooltip
                    />
                </span>
            </StyledBooleanSearchWrapper>
        </TopTableFiltersBarContainer>
    );
};
